import { UrlObject } from "url";

import {
  Box,
  Center,
  Drawer,
  DrawerBody,
  DrawerContent,
  HStack,
  IconButton,
  Stack,
  StackDivider,
  Text,
  useDisclosure,
} from "@chakra-ui/react";
import NextLink from "next/link";
import { useRouter } from "next/router";
import React, { FC, memo, ReactNode, useCallback, useEffect, useState } from "react";

import {
  Account,
  ArrowLeft,
  CheckInDetailHome,
  Cross,
  Hamburger,
  HeaderLogo,
  HistoryGray,
  Settings,
} from "../icons";

import { useCurrentAccount } from "src/entities/account/useCurrentAccount";
import { useHistory } from "src/hooks/useHistory";
import {
  CONTACT,
  EXTERNAL_TRANSMISSION,
  FAQ,
  MICIN_PRIVACY_POLICY,
  PRIVACY_POLICY,
  TERMS,
} from "src/urls";
import { pagesPath } from "src/utils/$path";
import { getAuthSettingsUrl } from "src/utils/getAuthUrl";

const MAIN_HEADER_HEIGHT = 44;
const SUB_HEADER_HEIGHT = 56;

type Props = {
  showLogoArea?: boolean;
  showMenuIcon?: boolean;
  showHeaderBottomBorder?: true;
  subHeader?: {
    availablePrevUrls?: UrlObject[];
    availablePrevUrlPatterns?: RegExp[];
    prevUrl?: UrlObject | null;
    showLogoArea?: boolean;
    showMenuIcon?: boolean;
    title: string;
    forceHistoryBack?: boolean;
  };
  white?: boolean;
  showTerms?: boolean;
  footerNoSpace?: boolean;
  additionalFooterElement?: React.ReactNode;
};

export const ContentContainer: FC<Props & { children: ReactNode }> = ({
  white,
  showLogoArea,
  showMenuIcon,
  showHeaderBottomBorder,
  subHeader,
  footerNoSpace,
  additionalFooterElement,
  children,
}) => {
  const bgColor = white ? "white" : "gray.50";

  const [position, setPosition] = useState(typeof window !== "undefined" ? window.pageYOffset : 0);
  const [visible, setVisible] = useState(true);

  const handleScroll = useCallback(() => {
    const moving = window.pageYOffset;

    setVisible(position > moving);
    setPosition(moving);
  }, [position]);

  useEffect(() => {
    window.addEventListener("scroll", handleScroll);

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, [handleScroll]);

  const header_height = (() => {
    let header_height = 0;

    if (showLogoArea) header_height += MAIN_HEADER_HEIGHT;
    if (!!subHeader) header_height += SUB_HEADER_HEIGHT;

    return header_height;
  })();

  return (
    <Stack
      bgColor={bgColor}
      minH="100vh"
      w="100vw"
      justify="space-between"
      align="center"
      spacing={footerNoSpace ? 0 : "96px"}
    >
      <Stack flex={1} w="full" spacing="0px">
        <Box
          position="sticky"
          top={visible ? 0 : `-${header_height}px`}
          bgColor="white"
          zIndex="sticky"
          transition="top 0.4s ease-out"
          filter="drop-shadow(0px 1px 0px rgba(0, 0, 0, 0.05))"
        >
          <Header
            showLogoArea={showLogoArea}
            showMenuIcon={showMenuIcon}
            subHeader={subHeader}
            showHeaderBottomBorder={showHeaderBottomBorder}
          />
        </Box>

        <Box flex={1} width="100%" maxWidth="600px" minWidth="320px" alignSelf="center">
          {children}
        </Box>
      </Stack>

      {additionalFooterElement}

      <Footer />
    </Stack>
  );
};

const LogoArea = memo<{ isOpen: boolean; shouldShowMenuIcon: boolean; onToggle(): void }>(
  ({ isOpen, shouldShowMenuIcon, onToggle }) => {
    return (
      <Stack
        direction="row"
        justify="space-between"
        alignItems="center"
        h={`${MAIN_HEADER_HEIGHT}px`}
        pl="12px"
        pr="4px"
      >
        <NextLink href={pagesPath.home.$url()}>
          <HeaderLogo />
        </NextLink>

        {shouldShowMenuIcon && (
          <IconButton
            variant="ghostWithoutBg"
            aria-label={isOpen ? "閉じる" : "開く"}
            onClick={onToggle}
            minH={`${MAIN_HEADER_HEIGHT}px`}
          >
            {isOpen ? <Cross /> : <Hamburger />}
          </IconButton>
        )}
      </Stack>
    );
  },
);

const Header = memo<
  Pick<Props, "showLogoArea" | "showMenuIcon" | "subHeader" | "showHeaderBottomBorder">
>(({ showLogoArea, showMenuIcon, subHeader, showHeaderBottomBorder }) => {
  const { isOpen, onOpen, onClose } = useDisclosure();

  return (
    <>
      <Stack
        bg="white"
        borderBottom={showHeaderBottomBorder && "1px solid"}
        borderColor="rgba(0, 0, 0, 0.05) "
      >
        {showLogoArea && (
          <LogoArea isOpen={false} shouldShowMenuIcon={showMenuIcon ?? false} onToggle={onOpen} />
        )}

        {!!subHeader && (
          <Center position="relative" py="16px">
            {(!!subHeader.prevUrl || subHeader.forceHistoryBack) && (
              <BackButton
                prevUrl={subHeader.prevUrl}
                forceHistoryBack={subHeader.forceHistoryBack}
                availablePrevUrls={subHeader.availablePrevUrls}
                availablePrevUrlPatterns={subHeader.availablePrevUrlPatterns}
              />
            )}

            {subHeader.showLogoArea && (
              <Stack position="absolute" left="8px">
                <NextLink href={pagesPath.home.$url()}>
                  <CheckInDetailHome />
                </NextLink>
              </Stack>
            )}

            <Text fontSize="16px" fontWeight="bold" color="gray.900">
              {subHeader.title}
            </Text>

            {subHeader.showMenuIcon && (
              <IconButton
                variant="ghostWithoutBg"
                position="absolute"
                right="8px"
                aria-label={isOpen ? "閉じる" : "開く"}
                onClick={onOpen}
              >
                {isOpen ? <Cross /> : <Hamburger />}
              </IconButton>
            )}
          </Center>
        )}
      </Stack>

      <MenuDrawer isOpen={isOpen} onClose={onClose} />
    </>
  );
});

const BackButton = memo<{
  prevUrl?: UrlObject | null;
  forceHistoryBack?: boolean;
  availablePrevUrls: UrlObject[] | undefined;
  availablePrevUrlPatterns: RegExp[] | undefined;
}>(({ prevUrl, availablePrevUrls, availablePrevUrlPatterns, forceHistoryBack }) => {
  const router = useRouter();
  const history = useHistory();

  const onClickBack = useCallback(() => {
    const { nextTop } = history.pop();

    if (!prevUrl || forceHistoryBack) {
      router.back();

      return;
    }

    const availableUrlPathnames = availablePrevUrls?.map((url) => url.pathname);

    const canHistoryBack =
      nextTop &&
      (nextTop.href === prevUrl.pathname ||
        availableUrlPathnames?.includes(nextTop.href) ||
        availablePrevUrlPatterns?.some((pattern) => pattern.test(nextTop.href)));

    // NOTE: state に保持している最新の prevUrl が該当ページから戻る可能性のあるページの urls に含まれる場合のみ
    // router.back() を呼び出し、ブラウザの history と state の最新を一致させる
    if (canHistoryBack) {
      router.back();
    } else {
      // NOTE: URL 直打ちなどの場合、state に保持している最新の prevUrl が availablePrevUrls に含まれない可能性がある。
      // その場合には該当ページからの遷移先を指定しておきそこに遷移する。
      router.push(prevUrl);
    }
  }, [availablePrevUrls, history, prevUrl, router, forceHistoryBack, availablePrevUrlPatterns]);

  return (
    <IconButton
      aria-label="back"
      position="absolute"
      left="8px"
      variant="link"
      onClick={onClickBack}
      icon={<ArrowLeft />}
    />
  );
});

const MenuDrawer = memo<{
  isOpen: boolean;
  onClose: () => void;
}>(({ isOpen, onClose }) => {
  const { currentAccount } = useCurrentAccount();
  const settingsUrl = getAuthSettingsUrl(typeof window !== "undefined" ? window.location.href : "");

  return (
    <Drawer
      placement="top"
      isOpen={isOpen}
      onClose={onClose}
      isFullHeight
      // eslint-disable-next-line jsx-a11y/no-autofocus
      autoFocus={false}
    >
      <DrawerContent px="0px">
        <DrawerBody p="0px 0px 100px 0px">
          <LogoArea isOpen shouldShowMenuIcon onToggle={onClose} />

          <HStack bgColor="primary.50" py="8px" px="16px">
            <Account />

            <Text fontSize="14px" fontWeight={400} color="gray.900" className="mask-mouseflow">
              {!!currentAccount?.email ? currentAccount.email : ""}
            </Text>
          </HStack>

          <Stack
            spacing="32px"
            pt="32px"
            px="16px"
            divider={<StackDivider borderColor="gray.200" />}
          >
            <Stack spacing="32px">
              <NextLink href={settingsUrl}>
                <HStack align="center" spacing="8px">
                  <Settings />

                  <Text fontSize="16px" fontWeight={400} color="gray.900">
                    設定
                  </Text>
                </HStack>
              </NextLink>

              <NextLink href={pagesPath.home.histories.$url().pathname}>
                <HStack align="center" spacing="8px">
                  <HistoryGray />

                  <Text fontSize="16px" fontWeight={400} color="gray.900">
                    ご利用履歴
                  </Text>
                </HStack>
              </NextLink>
            </Stack>

            <Stack spacing="32px">
              <a target="_blank" href={FAQ} rel="noopener noreferrer">
                <Text fontSize="16px" fontWeight={400} color="gray.900">
                  ヘルプ
                </Text>
              </a>

              <a target="_blank" href={CONTACT} rel="noopener noreferrer">
                <Text fontSize="16px" fontWeight={400} color="gray.900">
                  お問い合わせ
                </Text>
              </a>

              <a target="_blank" href={TERMS} rel="noopener noreferrer">
                <Text fontSize="16px" fontWeight={400} color="gray.900">
                  利用規約
                </Text>
              </a>

              <a target="_blank" href={MICIN_PRIVACY_POLICY} rel="noopener noreferrer">
                <Text fontSize="16px" fontWeight={400} color="gray.900">
                  プライバシーポリシー
                </Text>
              </a>

              <a target="_blank" href={EXTERNAL_TRANSMISSION} rel="noopener noreferrer">
                <Text fontSize="16px" fontWeight={400} color="gray.900">
                  外部送信ポリシー
                </Text>
              </a>

              <a target="_blank" href={PRIVACY_POLICY} rel="noopener noreferrer">
                <Text fontSize="16px" fontWeight={400} color="gray.900">
                  オンライン医療サービス個人情報保護方針
                </Text>
              </a>
            </Stack>
          </Stack>
        </DrawerBody>
      </DrawerContent>
    </Drawer>
  );
});

export const Footer = memo<Pick<Props, "showTerms">>(() => {
  return (
    <Center bg="gray.50" py="16px" w="full">
      <Text color="gray.600" fontSize="12px">
        &copy; MICIN,INC.
      </Text>
    </Center>
  );
});
